/**
 * Unit typeahead for the Zuordnung-hinzufügen and Zuordnung-verschieben
 * dialogs — Inventar 2.0, S7a.
 *
 * MOVED, NOT REWRITTEN. `unitAssignSearchBlob`, `filterUnitsForAssignSearch`
 * and `UnitAssignSearchHitList` come out of AdminInventoryDetailPage.js
 * unchanged apart from their colours. The search semantics S6a characterized
 * stay exactly as they are, including the two that look like bugs:
 *
 *  - the currently selected unit is force-kept at the top of the hit list even
 *    when it does not match the query (so a one-match query can read
 *    „Treffer (2)"), and
 *  - the list renders nothing at all when there are no hits; the separate
 *    „Keine passende Unit gefunden" line in the dialog takes over.
 *
 * `role="list"`, the accessible name „Gefilterte Units" and the literal
 * „Treffer (N)" counter are part of the contract E1/E2/E3/E5/E6 pin. The
 * counter text must stay the <p>'s only content — the test compares the whole
 * trimmed textContent.
 */
import React from "react";

import { normalizeUnit, formatUnitSelectOptionLabel } from "../../../api/adminData";
import { getUnitHeadingLabel } from "../../../utils/unitDisplayId";

function unitAssignSearchBlob(u) {
  if (!u) return "";
  const nu = normalizeUnit(u);
  const parts = [
    nu.id,
    nu.unitId,
    getUnitHeadingLabel(nu),
    formatUnitSelectOptionLabel(u),
    nu.shortUnitId,
    nu.short_unit_id,
    u.short_unit_id,
    u.unit_short_id,
    u.unit_code,
    u.external_unit_id,
    nu.title,
    u.title,
    nu.name,
    u.name,
    nu.place,
    nu.address,
    u.address,
    u.street,
    u.street_address,
    u.address_line1,
    nu.city,
    u.city,
    nu.zip,
    u.zip,
    u.postal_code,
  ];
  return parts
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).toLowerCase())
    .join(" ");
}

export function filterUnitsForAssignSearch(units, rawQuery, selectedUnitId) {
  const q = String(rawQuery || "").trim().toLowerCase();
  const list = Array.isArray(units) ? units : [];
  let out = q ? list.filter((u) => unitAssignSearchBlob(u).includes(q)) : [...list];
  const sel = String(selectedUnitId || "").trim();
  if (sel) {
    const hasSel = out.some((u) => {
      const nu = normalizeUnit(u);
      return String(nu.id || nu.unitId || "") === sel;
    });
    if (!hasSel) {
      const u0 = list.find((u) => {
        const nu = normalizeUnit(u);
        return String(nu.id || nu.unitId || "") === sel;
      });
      if (u0) out = [u0, ...out];
    }
  }
  return out;
}

/** Visible, clickable rows for filtered units (native select stays closed until opened). */
export function UnitAssignSearchHitList({ query, units, selectedUnitId, onSelectUnitId }) {
  const q = String(query || "").trim();
  if (!q || !Array.isArray(units) || units.length === 0) return null;
  return (
    <div
      className="mb-2 max-h-[220px] overflow-y-auto rounded-[8px] border border-border bg-muted p-1 [scrollbar-width:thin]"
      role="list"
      aria-label="Gefilterte Units"
    >
      <p className="m-0 px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        Treffer ({units.length})
      </p>
      {units.map((u) => {
        const nu = normalizeUnit(u);
        const id = nu.id || nu.unitId;
        const idStr = id == null ? "" : String(id);
        const sel = String(selectedUnitId || "") === idStr;
        return (
          <button
            key={idStr}
            type="button"
            onClick={() => onSelectUnitId(idStr)}
            className={`mb-0.5 w-full rounded-[6px] border px-2 py-2 text-left text-[13px] font-medium normal-case tracking-normal last:mb-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              sel
                ? "border-primary bg-primary/10 text-foreground"
                : "border-transparent text-foreground hover:border-border hover:bg-card"
            }`}
          >
            {formatUnitSelectOptionLabel(u)}
          </button>
        );
      })}
    </div>
  );
}
